import {AfterViewInit, Component, Input, OnInit} from '@angular/core';
import {ToastService, TranslationService} from '@emma-labs/shared/angular';
import {
  Coverage,
  ErrorResponseCode,
  extractCoverageFromProtection,
  InsuranceCoverage,
  isWholeLife,
  PersonInsuranceType,
  Protection,
  RiderCoverage,
  RiderType,
  Version,
} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CardModule} from 'primeng/card';
import {debounceTime, first, Subject} from 'rxjs';
import {ArchivedProposalPageOrchestrator} from '../../../services/proposal/archived-proposal-page.orchestrator';
import {DEBOUNCE_TIME} from '../../../utils/utility-constants';
import {
  getSelectedCoverageAmount,
  getUpdatedLifeInsuranceCoverage,
  getUpdatedRiderCoverage,
} from '../../proposal-page/coverage-builder-section/coverage-builder-utils';
import {TermLifeCardComponent} from '../../proposal-page/coverage-builder-section/life-insurance/term-life/term-life-card.component';
import {WholeLifeCardComponent} from '../../proposal-page/coverage-builder-section/life-insurance/whole-life/whole-life-card.component';
import {ChildRiderCardComponent} from '../../proposal-page/coverage-builder-section/rider/child-rider/child-rider-card.component';
import {SelectedProductSectionComponent} from '../../proposal-page/coverage-builder-section/selected-product-section/selected-product-section.component';

@UntilDestroy()
@Component({
  selector: 'em-coverage-modifier-section',
  templateUrl: './coverage-modifier-section.component.html',
  imports: [
    CardModule,
    SelectedProductSectionComponent,
    WholeLifeCardComponent,
    TermLifeCardComponent,
    ChildRiderCardComponent,
  ],
})
export class CoverageModifierSectionComponent implements AfterViewInit, OnInit {
  private coverageUpdateSubject = new Subject<Coverage>();

  @Input({required: true}) set selectedProtection(
    _selectedProtection: Protection & {surchargeRate?: number},
  ) {
    this._selectedProtection = _selectedProtection;
    this.selectedCoverage = extractCoverageFromProtection(this._selectedProtection);
    this.selectedWholeLifeCoverageAmount = getSelectedCoverageAmount(this.selectedCoverage, 'WHOLE');
    this.selectedTermLifeCoverageAmount = getSelectedCoverageAmount(this.selectedCoverage, 'TERM');

    const riders = this.selectedCoverage.riders;
    this.selectedChildRiderCoverageAmount = riders?.find((rider) => rider.type === RiderType.CHILD)?.coverage;
  }

  @Input({required: true}) ratesVersion!: Version;

  @Input({required: true}) set archivedProtection(protection: Protection) {
    this._archivedProtection = protection;
    this.hasWholeLife = protection.personInsurances.some((pi) => isWholeLife(pi.duration));
    this.hasTermLife = protection.personInsurances.some((pi) => !isWholeLife(pi.duration));
    this.hasChildRider = protection.personInsurances?.some((pi) =>
      pi.riders?.some((rider) => rider.type === RiderType.CHILD),
    );
  }

  _selectedProtection!: Protection & {surchargeRate?: number};
  _archivedProtection!: Protection;
  selectedWholeLifeCoverageAmount: number | undefined;
  selectedTermLifeCoverageAmount: number | undefined;
  selectedChildRiderCoverageAmount: number | undefined;
  hasWholeLife = false;
  hasTermLife = false;
  hasChildRider = false;
  private selectedCoverage: Coverage = {lifeInsurances: {}};

  constructor(
    private readonly orchestrator: ArchivedProposalPageOrchestrator,
    private readonly translate: TranslationService,
    private readonly toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.coverageUpdateSubject.pipe(untilDestroyed(this), debounceTime(DEBOUNCE_TIME)).subscribe((coverage) =>
      this.orchestrator
        .updateProtection(coverage)
        .pipe(first())
        .subscribe({
          error: (error) => {
            if (error.code === ErrorResponseCode.COVERAGE_EXCEEDS_MAXIMUM) {
              this.toastService.error(
                this.translate.currentLanguageIsFr()
                  ? 'Les nouvelles couvertures ne peuvent pas dépasser les couvertures existantes.'
                  : 'Updated coverages cannot exceed the existing coverages.',
              );
            } else {
              this.toastService.error(
                this.translate.currentLanguageIsFr()
                  ? "Une erreur s'est produite lors de la mise à jour des couvertures."
                  : 'An error occurred while updating the coverages.',
              );
            }
          },
        }),
    );
  }

  ngAfterViewInit(): void {
    if (!this._selectedProtection || !this.ratesVersion) {
      throw new Error('Missing required input');
    }
  }

  updateLifeInsurance($event: Omit<InsuranceCoverage, 'duration'>, type: PersonInsuranceType): void {
    // Cannot use the duration in the event because the card component emits the default (T10 for term)
    const {amount} = $event;

    const duration =
      type === 'WHOLE'
        ? this._archivedProtection.personInsurances.find((pi) => isWholeLife(pi.duration))?.duration
        : this._archivedProtection.personInsurances.find((pi) => !isWholeLife(pi.duration))?.duration;

    if (!duration) {
      throw new Error('Could not find duration');
    }

    this.selectedCoverage = getUpdatedLifeInsuranceCoverage(this.selectedCoverage, amount, duration);
    this.coverageUpdateSubject.next(this.selectedCoverage);
  }

  updateRider($event: RiderCoverage): void {
    const {type, amount, duration} = $event;

    this.selectedCoverage = getUpdatedRiderCoverage(this.selectedCoverage, amount, duration, type);
    this.coverageUpdateSubject.next(this.selectedCoverage);
  }
}
